<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { positionApi } from '@/entities/Position';
  import { functionalActivityApi } from '@/entities/FunctionalActivity';
  import { FilterModal, IconImage, IconRadio, PositionSelector, ThemeProvider } from '@/shared/ui';
  import ArrowRight from '@/shared/assets/icons/ArrowRight.svg';
  import type {
    ListPermissionsModalCriterion,
    ListPermissionsModalPayload,
  } from './ListPermissionsModal.types';

  interface SelectableItem {
    id: number;
    name: string;
  }

  defineProps<{
    title?: string;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'apply', payload: ListPermissionsModalPayload): void;
  }>();

  const criterion = ref<ListPermissionsModalCriterion>('registrationNumber');
  const registrationNumber = ref('');
  const selectedPositionIds = ref<string[]>([]);
  const selectedFunctionalActivityIds = ref<string[]>([]);
  const hasAttemptedSubmit = ref(false);
  const positionItems = ref<SelectableItem[]>([]);
  const functionalActivityItems = ref<SelectableItem[]>([]);
  const isSelectorLoading = ref(false);
  const hasSelectorError = ref(false);

  const sanitizeRegistrationNumber = (value: string) => value.replace(/\D/g, '');

  const getPaddedRegistrationNumber = (value: number, minLength: number) =>
    String(Math.max(value, 0)).padStart(minLength, '0');

  const isFormValid = computed(() => {
    if (criterion.value === 'registrationNumber') {
      return registrationNumber.value.trim().length > 0;
    }

    if (criterion.value === 'position') {
      return selectedPositionIds.value.length > 0;
    }

    return selectedFunctionalActivityIds.value.length > 0;
  });

  const validationMessage = computed(() => {
    if (!hasAttemptedSubmit.value || isFormValid.value) {
      return '';
    }

    if (criterion.value === 'registrationNumber') {
      return 'Informe uma matrícula para continuar.';
    }

    if (criterion.value === 'position') {
      return 'Selecione ao menos um cargo para continuar.';
    }

    return 'Selecione ao menos uma atividade funcional para continuar.';
  });

  const resetForm = () => {
    criterion.value = 'registrationNumber';
    registrationNumber.value = '';
    selectedPositionIds.value = [];
    selectedFunctionalActivityIds.value = [];
    hasAttemptedSubmit.value = false;
  };

  const handleClose = () => {
    resetForm();
    emit('close');
  };

  const handleApply = () => {
    hasAttemptedSubmit.value = true;

    if (!isFormValid.value) {
      return;
    }

    if (criterion.value === 'registrationNumber' && registrationNumber.value.trim()) {
      emit('apply', {
        criterion: 'registrationNumber',
        registrationNumber: registrationNumber.value,
      });
      return;
    }

    if (criterion.value === 'position') {
      emit('apply', {
        criterion: 'position',
        positionIds: selectedPositionIds.value.map((value) => Number(value)),
      });
      return;
    }

    emit('apply', {
      criterion: 'functionalActivity',
      functionalActivityIds: selectedFunctionalActivityIds.value.map((value) => Number(value)),
    });
  };

  const handleCriterionChange = (value: string) => {
    criterion.value = value as ListPermissionsModalCriterion;
    registrationNumber.value = '';
    selectedPositionIds.value = [];
    selectedFunctionalActivityIds.value = [];
    hasAttemptedSubmit.value = false;
  };

  const handlePositionSelectionChange = (value: string[]) => {
    selectedPositionIds.value = [...value];
  };

  const handleFunctionalActivitySelectionChange = (value: string[]) => {
    selectedFunctionalActivityIds.value = [...value];
  };

  const handleRegistrationNumberInput = (event: Event) => {
    registrationNumber.value = sanitizeRegistrationNumber((event.target as HTMLInputElement).value);
    if (registrationNumber.value.length > 6) {
      registrationNumber.value = registrationNumber.value.slice(0, 6);
    }
  };

  const handleRegistrationNumberBeforeInput = (event: InputEvent) => {
    if (event.data && /\D/.test(event.data)) {
      event.preventDefault();
    }
  };

  const incrementRegistrationNumber = () => {
    const currentValue = registrationNumber.value || '0';
    const nextValue = Number.parseInt(currentValue, 10) + 1;
    registrationNumber.value = getPaddedRegistrationNumber(nextValue, currentValue.length);
  };

  const decrementRegistrationNumber = () => {
    if (!registrationNumber.value) {
      return;
    }

    const nextValue = Math.max(Number.parseInt(registrationNumber.value, 10) - 1, 0);
    registrationNumber.value = getPaddedRegistrationNumber(
      nextValue,
      registrationNumber.value.length
    );
  };

  const modalTitle = computed(() => 'Listar Permissões');

  const loadSelectorItems = async (mode: 'positions' | 'functionalActivities') => {
    isSelectorLoading.value = true;
    hasSelectorError.value = false;

    try {
      if (mode === 'positions') {
        const response = await positionApi.searchPositions({
          active: true,
          type: 'POSITION',
          sort: 'name',
          order: 'asc',
          page: 1,
          limit: 1000,
        });
        positionItems.value = response.data.map((position) => ({
          id: position.id,
          name: position.name,
        }));
        return;
      }

      const response = await functionalActivityApi.search({
        active: true,
        sort: 'name',
        order: 'asc',
        page: 1,
        limit: 1000,
      });
      functionalActivityItems.value = response.data.map((activity) => ({
        id: activity.id,
        name: activity.name,
      }));
    } catch {
      hasSelectorError.value = true;
      if (mode === 'positions') {
        positionItems.value = [];
      } else {
        functionalActivityItems.value = [];
      }
    } finally {
      isSelectorLoading.value = false;
    }
  };

  watch(
    criterion,
    (currentCriterion) => {
      if (currentCriterion === 'position') {
        loadSelectorItems('positions');
      }

      if (currentCriterion === 'functionalActivity') {
        loadSelectorItems('functionalActivities');
      }
    },
    { immediate: true }
  );

  defineExpose({
    criterion,
    registrationNumber,
    selectedPositionIds,
    selectedFunctionalActivityIds,
    handleApply,
    handleClose,
    handleCriterionChange,
  });
</script>

<template>
  <FilterModal
    content-class="w-[900px]"
    :buttons-text="{ clear: 'Cancelar', apply: 'Listar permissões', cancel: 'Cancelar' }"
    :apply-disabled="!isFormValid"
    :replace-clear-with-cancel="true"
    @close="handleClose"
    @apply-filters="handleApply"
  >
    <template #content>
      <ThemeProvider class="min-h-0 overflow-y-auto">
        <div class="flex flex-col gap-6 px-6 py-6">
          <h4 class="text-base font-semibold text-gray-07">
            {{ title || modalTitle }}
          </h4>

          <div class="space-y-4">
            <p class="text-sm font-semibold text-gray-07">
              Filtrar por
            </p>

            <div class="flex flex-wrap gap-6">
              <IconRadio
                name="list-permissions-criterion"
                label="Matrícula"
                value="registrationNumber"
                :model-value="criterion"
                @update:model-value="handleCriterionChange"
              />
              <IconRadio
                name="list-permissions-criterion"
                label="Cargo"
                value="position"
                :model-value="criterion"
                @update:model-value="handleCriterionChange"
              />
              <IconRadio
                name="list-permissions-criterion"
                label="Atividade Funcional"
                value="functionalActivity"
                :model-value="criterion"
                @update:model-value="handleCriterionChange"
              />
            </div>
          </div>

          <div
            v-if="criterion === 'registrationNumber'"
            class="space-y-2"
          >
            <label
              for="permissions-registration-number"
              class="text-sm font-semibold text-gray-07"
            >
              Matrícula
            </label>
            <div class="relative w-[150px]">
              <input
                id="permissions-registration-number"
                data-testid="registration-number-input"
                :value="registrationNumber"
                type="text"
                inputmode="numeric"
                class="w-[150px] rounded-lg border border-gray-02 px-4 py-3 pr-10 text-gray-03 [appearance:textfield]"
                aria-label="Matrícula"
                placeholder="000000"
                @beforeinput="handleRegistrationNumberBeforeInput"
                @input="handleRegistrationNumberInput"
              >
              <div class="absolute right-3 top-1/2 flex -translate-y-1/2 flex-col">
                <IconImage
                  :src="ArrowRight"
                  alt="Aumentar"
                  class="h-6 w-6 rotate-270 cursor-pointer -ml-[1.52px]"
                  @click="incrementRegistrationNumber"
                />
                <IconImage
                  :src="ArrowRight"
                  alt="Diminuir"
                  class="h-6 w-6 rotate-90 cursor-pointer"
                  @click="decrementRegistrationNumber"
                />
              </div>
            </div>
          </div>

          <PositionSelector
            v-else-if="criterion === 'position'"
            mode="positions"
            title="Cargos"
            height-class="h-[min(27rem,38vh)]"
            :items="positionItems"
            :is-loading="isSelectorLoading"
            :has-error="hasSelectorError"
            :selected-ids="selectedPositionIds"
            @update:selected-ids="handlePositionSelectionChange"
          />

          <PositionSelector
            v-else
            mode="functionalActivities"
            title="Atividades Funcionais"
            height-class="h-[min(27rem,38vh)]"
            :items="functionalActivityItems"
            :is-loading="isSelectorLoading"
            :has-error="hasSelectorError"
            :selected-ids="selectedFunctionalActivityIds"
            @update:selected-ids="handleFunctionalActivitySelectionChange"
          />

          <p
            v-if="validationMessage"
            data-testid="list-permissions-validation"
            class="text-sm text-red-500"
          >
            {{ validationMessage }}
          </p>
        </div>
      </ThemeProvider>
    </template>
  </FilterModal>
</template>

<style scoped>
  .vfm__overlay {
    background-color: rgba(0, 0, 0, 0.5);
  }
</style>
